// Web shim for react-native-video
// Provides a fallback HTML5 video component for web

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

export interface VideoProps {
  source?: { uri?: string };
  style?: any;
  controls?: boolean;
  paused?: boolean;
  onLoad?: (data: any) => void;
  onLoadStart?: () => void;
  onError?: (error: any) => void;
  onProgress?: (data: any) => void;
  onBuffer?: (data: any) => void;
  onEnd?: () => void;
  volume?: number;
  muted?: boolean;
  rate?: number;
  [key: string]: any;
}

const Video = React.forwardRef<any, VideoProps>(
  ({
    source,
    style,
    controls = true,
    paused = false,
    onLoad,
    onLoadStart,
    onError,
    volume = 1,
    muted = false,
    ...props
  }, ref) => {
    const videoRef = React.useRef<HTMLVideoElement>(null);

    React.useImperativeHandle(ref, () => ({
      seek: (time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      pause: () => {
        if (videoRef.current) {
          videoRef.current.pause();
        }
      },
      resume: () => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      },
    }));

    const handleCanPlay = () => {
      if (onLoad) {
        onLoad({
          duration: videoRef.current?.duration || 0,
          currentTime: 0,
          naturalSize: {
            width: videoRef.current?.videoWidth || 0,
            height: videoRef.current?.videoHeight || 0,
          },
        });
      }
    };

    React.useEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = volume;
        videoRef.current.muted = muted;
        if (paused) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
      }
    }, [volume, muted, paused]);

    return (
      <View style={[{ backgroundColor: '#000' }, style]}>
        <video
          ref={videoRef}
          src={source?.uri}
          controls={controls}
          style={{ width: '100%', height: '100%' }}
          onLoadStart={onLoadStart}
          onCanPlay={handleCanPlay}
          onError={(e) => onError && onError(e)}
          {...props}
        />
      </View>
    );
  }
);

Video.displayName = 'Video';

export default Video;
